import React, { useEffect, useState } from 'react';
import { Box, Button, MenuItem, Select, SelectChangeEvent, Typography } from '@mui/material';
import ArrowUp from '@mui/icons-material/ArrowUpward';
import ArrowDown from '@mui/icons-material/ArrowDownward';
import { useReviewUpdatesContext } from '../context';
import { useNavigate } from 'react-router-dom';
import { translate as t } from 'hooks/useTranslations';
import _ from 'lodash';

export const ToolbarLeft: React.FC = () => {
  const { focus, setFocus, sidebarItems, policyId } = useReviewUpdatesContext();
  const [action, setAction] = useState<string | null>(focus);

  const handleActionsChange = (event: SelectChangeEvent) => {
    const newFocusId = event.target.value;
    setAction(newFocusId);
    setFocus(newFocusId);
  };

  useEffect(() => {
    if (focus) {
      setAction(focus);
      const targetElement = document.getElementById(`change_${focus}`);
      if (targetElement) {
        targetElement.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [focus]);

  if (_.isEmpty(policyId) || sidebarItems.length === 0) {
    return null;
  }

  return (
    <Select
      labelId='sections-menu-label'
      id='sections-menu'
      value={action || ''}
      onChange={handleActionsChange}
      label={t('draftManuals.reviewUpdates.toolbar.sections')}
      displayEmpty
      style={{ height: 30 }}
    >
      <MenuItem
        value=''
      >
        {t('draftManuals.reviewUpdates.toolbar.sections')}
      </MenuItem>
      {sidebarItems.map((item) => (
        <MenuItem
          key={item.id}
          style={{ fontWeight: 500 }}
          value={item.id}
        >
          {item.title}
        </MenuItem>
      ))}
    </Select>
  );
};

export const ToolbarRight: React.FC = () => {
  const { focus, setFocus, sidebarItems, handleDone, policyId, isLoading, isRightDrawerOpen, setIsRightDrawerOpen } =
    useReviewUpdatesContext();

  const handleNextSection = () => {
    const item = sidebarItems.find((x) => x.id === focus);
    const currentIndex = _.indexOf(sidebarItems, item);
    if (currentIndex >= 0 && currentIndex < sidebarItems.length - 1) {
      setFocus(sidebarItems[currentIndex + 1].id);
    }
  };

  const handlePrevSection = () => {
    const item = sidebarItems.find((x) => x.id === focus);
    const currentIndex = _.indexOf(sidebarItems, item);
    if (currentIndex > 0) {
      setFocus(sidebarItems[currentIndex - 1].id);
    }
  };

  const onHandleDone = async () => {
    await handleDone();
  };

  const pendingList = sidebarItems;
  const currentIndex = pendingList.findIndex((change) => change.id === focus);
  const disablePrev = pendingList.length <= 1 || currentIndex === 0;
  const disableNext = pendingList.length <= 1 || currentIndex === pendingList.length - 1;

  return (
    <Box sx={{ display: 'flex', gap: 2 }}>
      {!isRightDrawerOpen && (
        <Button
          variant='outlined'
          size='small'
          onClick={() => setIsRightDrawerOpen(true)}
        >
          {t('draftManuals.reviewUpdates.toolbar.allUpdates')}
        </Button>
      )}
      {!_.isEmpty(policyId) && sidebarItems.length > 0 && (
        <>
          <Button
            startIcon={<ArrowUp></ArrowUp>}
            variant='outlined'
            size='small'
            onClick={handlePrevSection}
            disabled={disablePrev}
          >
            {t('draftManuals.reviewUpdates.toolbar.prevUpdate')}
          </Button>
          <Button
            endIcon={<ArrowDown></ArrowDown>}
            variant='outlined'
            size='small'
            onClick={handleNextSection}
            disabled={disableNext}
          >
            {t('draftManuals.reviewUpdates.toolbar.nextUpdate')}
          </Button>
        </>
      )}
      <Button
        size='small'
        disabled={isLoading}
        onClick={onHandleDone}
      >
        {t('draftManuals.reviewUpdates.toolbar.done')}
      </Button>
    </Box>
  );
};
